// src/pages/HomePage.js
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { SERVICES, FLY_TIPPING_STATS, TRUST_POINTS, LEADERBOARD } from '../data/mockData';
import { HOME_DESCRIPTION, HOME_PATH, HOME_TITLE, homeJsonLd } from '../data/homeSeo';
import BrandName from '../components/BrandName';
import RegisterCheck from '../components/RegisterCheck';
import Seo from '../components/Seo';
import InstallApp from '../components/InstallApp';
import './HomePage.css';

const HOW_IT_WORKS = [
  { step: '01', title: 'Post a Job',        desc: 'Describe what you need — waste, removals, garden or scrap. It takes about a minute, in the browser. Posting is free and does not need an app.' },
  { step: '02', title: 'Get Bids',          desc: 'Local carriers can send a price, ETA and eco rating. Waste carriers are checked against the public register before they\'re approved.' },
  { step: '03', title: 'Choose & Track',    desc: 'Pick a carrier if you want to, follow the job, and keep the waste transfer note as a record. A note is not a guarantee.' },
  { step: '04', title: 'Carriers Compete',  desc: 'Carriers are ranked by price, speed and eco score. Competition is there to keep prices sharp. It does not promise a bid.' },
];

const HomePage = () => {
  const { user } = useAuth();
  // Route logged-in users to their dashboard instead of signup
  const customerDest = user ? (user.type === 'provider' ? '/provider-dashboard' : '/customer-dashboard') : '/signup';
  const providerDest = user ? (user.type === 'provider' ? '/provider-dashboard' : '/customer-dashboard') : '/provider-signup';
  return (
  <div className="home">
    <Seo title={HOME_TITLE} description={HOME_DESCRIPTION} path={HOME_PATH} jsonLd={homeJsonLd()} />

    {/* HERO */}
    <section className="hero">
      <div className="hero-bg-lines" />
      <div className="hero-content">
        <div className="hero-eyebrow">The UK's Waste & Removals Marketplace</div>
        <h1 className="hero-title">Let Waste Carriers<br /><span>Compete For Your Job</span></h1>
        <p className="hero-sub">
          Post a job in your browser. <br />
          Local carriers can send a price. You choose one, or none.
        </p>
        <p className="hero-audience">Built for homeowners, landlords &amp; businesses across the UK</p>
        <div className="hero-ctas">
          <Link to={customerDest} className="btn btn-primary">Post a job</Link>
          <Link to="/services" className="btn btn-secondary">VIEW SERVICES</Link>
        </div>
        <p className="hero-how-link"><Link to="/how-it-works" className="btn btn-secondary">See how a job works</Link></p>
        <InstallApp />
        <div className="hero-trust-line">
          Waste carriers are checked against the public register before they&apos;re approved.
        </div>
        <div className="hero-trust-pills">
          <span className="htpill">✅ Register check before approval</span>
          <span className="htpill">🛡️ Duty of care stays with you</span>
          <span className="htpill">♻️ Eco Leaderboard</span>
          <span className="htpill">📋 Waste Transfer Notes</span>
        </div>
      </div>
      <div className="hero-trucks">
        <span className="truck truck-l">🚛</span>
        <span className="truck truck-r">🚛</span>
      </div>
    </section>

    {/* LOCAL FOCUS BAND */}
    <section className="local-band">
      <div className="local-band-inner">
        <div className="local-band-left">
          <span className="local-tag">📍 Based in South Wales</span>
          <h3>Serving Swansea, Cardiff, Neath, Port Talbot & surrounding areas</h3>
          <p>Connecting local customers with carriers across South Wales. Waste carriers are checked against the public register before they&apos;re approved.</p>
        </div>
        <div className="local-band-right">
          <div className="local-stat"><span>🚐</span> Local carriers</div>
          <div className="local-stat"><span>⚡</span> Post in the browser</div>
          <div className="local-stat"><span>♻️</span> Eco-rated carriers</div>
        </div>
      </div>
    </section>

    {/* FLY-TIPPING STATS */}
    <section className="stats-band">
      {FLY_TIPPING_STATS.map((s, i) => (
        <div key={i} className="stat-item">
          <div className="stat-val">{s.val}</div>
          <div className="stat-label">{s.label}</div>
        </div>
      ))}
    </section>

    {/* TRUST / WHY WASTEGAME */}
    <section className="home-section trust-section">
      <div className="page-wrapper">
        <div className="trust-header">
          <div className="section-title">WHY <BrandName inline />?</div>
          <p className="trust-sub">
            Unlike Facebook Marketplace or a random search, waste carriers on <BrandName inline />
            {' '}are checked against the public register before they&apos;re approved.{' '}
            <RegisterCheck />
            {' '}That check is not a guarantee. Scrap collection is free. Your duty of care stays with you.
          </p>
        </div>
        <div className="trust-grid">
          {TRUST_POINTS.map((t, i) => (
            <div key={i} className="trust-card">
              <div className="trust-icon">{t.icon}</div>
              <h3>{t.title}</h3>
              <p>{t.desc}</p>
            </div>
          ))}
        </div>
        <div className="duty-care-banner">
          <div className="dcb-left">
            <div className="dcb-title">⚖️ The Law Is Clear</div>
            <p>
              Under the <strong>Environmental Protection Act 1990</strong>, it is your responsibility as a
              householder or business to ensure your waste is transferred to an authorised person.
              If your waste is fly-tipped, <strong>you could face prosecution</strong>.
              <BrandName inline /> keeps a record of the job. That record is not a guarantee the waste was handled legally.
            </p>
          </div>
          <Link to={customerDest} className="btn btn-primary">Post a job</Link>
        </div>
      </div>
    </section>

    {/* SERVICES */}
    <section className="home-section" style={{background:'var(--dark2)'}}>
      <div className="page-wrapper">
        <div className="section-title">OUR <span>SERVICES</span></div>
        <div className="services-grid">
          {SERVICES.map(svc => (
            <Link to="/services" key={svc.id} className="svc-card">
              <div className="svc-icon">{svc.icon}</div>
              <div className="svc-name">{svc.name}</div>
              <div className="svc-desc">{svc.description}</div>
              <div className="svc-credits">+{svc.credits} Eco Credits</div>
            </Link>
          ))}
        </div>
      </div>
    </section>

    {/* HOW IT WORKS */}
    <section className="home-section how-section">
      <div className="page-wrapper">
        <div className="section-title">HOW IT <span>WORKS</span></div>
        <div className="how-grid">
          {HOW_IT_WORKS.map((h, i) => (
            <div key={i} className="how-card">
              <div className="how-step">{h.step}</div>
              <h3>{h.title}</h3>
              <p>{h.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* LEADERBOARD TEASER */}
    <section className="home-section leaderboard-teaser" style={{background:'var(--dark2)'}}>
      <div className="page-wrapper">
        <div className="lb-teaser-inner">
          <div className="lb-teaser-text">
            <div className="section-title">THIS WEEK'S <span>TOP CARRIERS</span></div>
            <p className="lb-teaser-sub">
              Carriers earn points for every job won, 5-star review and eco-friendly disposal
            </p>
            <p>
              Carriers don&apos;t just compete on price — they compete on <strong>recycling performance</strong>.
              The best eco-rated carriers rise to the top.
              Waste carriers are checked against the public register before they&apos;re approved.
            </p>
            <div className="lb-teaser-stats">
              <div className="lbts"><div className="lbts-val">98%</div><div className="lbts-lbl">Illustrative top recycle rate</div></div>
              <div className="lbts"><div className="lbts-val">Check</div><div className="lbts-lbl">Public register, before approval</div></div>
            </div>
            <Link to={providerDest} className="btn btn-primary" style={{marginTop:'20px', display:'inline-block'}}>JOIN AS A CARRIER</Link>
          </div>
          <div className="lb-teaser-board">
            {LEADERBOARD.slice(0,5).map(c => (
              <div key={c.rank} className={`lb-row ${c.isMe ? 'lb-me' : ''}`}>
                <div className={`lb-rank r${c.rank <= 3 ? c.rank : ''}`}>{c.rank}</div>
                <div className="lb-av">{c.initials}</div>
                <div className="lb-info">
                  <div className="lb-name">
                    {c.name}
                  </div>
                  <div className="lb-sub">{c.jobs} jobs · illustrative</div>
                  <div className="lb-bar-bg"><div className="lb-bar" style={{width:`${(c.credits/412)*100}%`}} /></div>
                </div>
                <div className="lb-cred">{c.credits}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* CARRIER CTA */}
    <section className="home-section carrier-cta-section">
      <div className="page-wrapper">
        <div className="carrier-cta-inner">
          <div>
            <div className="section-title">ARE YOU A <span>LICENSED CARRIER?</span></div>
            <p>Join <BrandName inline /> and start winning local jobs today. Waste carriers are checked against the public register before they&apos;re approved. Build your eco reputation and climb the leaderboard.</p>
            <div className="carrier-cta-features">
              <span>✓ Free to join</span>
              <span>✓ Bid on local jobs</span>
              <span>✓ Earn Eco Credits</span>
              <span>✓ Build your reputation</span>
            </div>
          </div>
          <div className="carrier-cta-box">
            <div className="ccb-title">Requirements to Join</div>
            <div className="ccb-item">✅ Valid waste carrier registration (England or Wales)</div>
            <div className="ccb-item">✅ Public liability insurance</div>
            <Link to={providerDest} className="btn btn-primary btn-full" style={{marginTop:'16px'}}>APPLY AS CARRIER</Link>
          </div>
        </div>
      </div>
    </section>

    {/* FINAL CTA */}
    <section className="cta-band">
      <div className="cta-band-inner">
        <h2>READY TO JOIN THE WASTE<span>GAME</span>?</h2>
        <p>Join thousands of customers and carriers across South Wales and beyond.</p>
        <div className="hero-ctas">
          <Link to={customerDest} className="btn btn-primary">Post a job</Link>
          <Link to={providerDest} className="btn btn-secondary">BECOME A CARRIER</Link>
        </div>
      </div>
    </section>

  </div>
  );
};

export default HomePage;
